import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MaterialIcon from "../../../components/MaterialIcon";
import { callAPI } from "../../../helper/apiUtils";
import CockpitBriefingText, { BOOKMARKED_PRIORITIES_ID } from "./CockpitBriefingText";
import { CockpitBookmarkedPriorities } from "./CockpitTodoLists";
import { cockpitGroupOf, cockpitTaskDay } from "../priorityBookmarks";
import { Card, Chip, EmptyState, SecondaryButton, Skeleton } from "./CockpitPrimitives";
import { fill, formatAsOf, formatRelativeDay, stripHtml } from "../cockpitFormat";


/**
 * "Tagesberichte" — the daily briefings (systemTag daily_briefing).
 * The cockpit is the only place reports are listed (no Berichte tab in
 * Nachrichten any more). The latest report is shown as text. Each priority
 * can be turned into a to-do. Expanding an older report marks it read.
 *
 * `reports`  rows from GET /email/messages?folder=BRIEFING (list shape, no body)
 * `latest`   overview.latestDailyBriefing (full body) — merged in case it is
 *            not part of the folder list (e.g. a per-room report).
 */
const reportId = (r) => r && (r.id || r._id || r.messageId);
const isRead = (r) => r && (r.isRead === true || r.isRead === 1 || r.isRead === "1");
const REPORT_LOOKBACK_MS = 24 * 60 * 60 * 1000;

function activityMs(value) {
  const time = new Date(value || 0).getTime();
  return Number.isFinite(time) ? time : 0;
}

function inReportWindow(value, now) {
  const time = activityMs(value);
  return time > 0 && now - time <= REPORT_LOOKBACK_MS && time <= now + 60000;
}

/**
 * Rooms the daily-report picker can offer when the API has not yet sent
 * `reportActiveRooms`: a chat in the last 24 hours, or an email filed into
 * the room in that same window. The room list only carries the last chat per
 * room and the mailbox preview is capped, so an empty result means "unknown",
 * not "nothing happened" — the caller then keeps every room selectable.
 */
export function roomsWithReportActivity(rooms, recentEmails, now = Date.now()) {
  const known = new Map();
  (rooms || []).forEach((room) => {
    if (room && room.id != null) known.set(String(room.id), room);
  });
  const best = new Map();
  const note = (room, at) => {
    if (!inReportWindow(at, now) || !room || room.id == null) return;
    const key = String(room.id);
    const full = known.get(key) || room;
    if (!full.name) return;
    const time = activityMs(at);
    const prev = best.get(key);
    if (!prev || time > prev.time) best.set(key, { room: full, time });
  };
  (rooms || []).forEach((room) => note(room, room.lastActivityAt));
  (recentEmails || []).forEach((mail) => {
    ((mail && mail.rooms) || []).forEach((room) => note(room, mail.receivedAt));
  });
  return [...best.values()]
    .sort((a, b) => b.time - a.time || String(a.room.name).localeCompare(String(b.room.name), "de"))
    .map((row) => row.room);
}

function asText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (value && value.type === "Buffer" && Array.isArray(value.data)) {
    try {
      return new TextDecoder("utf-8").decode(Uint8Array.from(value.data));
    } catch (_) {
      return "";
    }
  }
  return "";
}

function reportHtml(report) {
  if (!report) return "";
  return asText(report.bodyHtml) || asText(report.html) || asText(report.bodyText) || asText(report.body);
}

/** List rows have a subject and a one-line snippet, not the report body. */
function isFullReport(report) {
  return reportHtml(report).trim().length > 0;
}

function roomLabel(report, strings) {
  if (!report) return "";
  if (report.roomName) return report.roomName;
  const linked = Array.isArray(report.linkedProjects) ? report.linkedProjects[0] : null;
  if (linked && linked.name) return linked.name;
  return strings.cockpit_report_all_projects || "Alle Projekte";
}

function phaseProgress(phase, itemCount) {
  const value = String(phase || "");
  if (value === "queued") return 8;
  if (value === "collecting") return 18;
  if (value === "reading_stored_text") return 30;
  if (value === "priorities") return 92;
  if (value.startsWith("summarizing")) return Math.min(88, 38 + Math.min(Number(itemCount) || 0, 50));
  return 12;
}

// What the report is doing right now, in the words of the person waiting for
// it. The server names the kind of content it is reading (summarizing_email …).
function phaseLabel(phase, strings) {
  const value = String(phase || "");
  if (value === "summarizing_email") return strings.cockpit_report_progress_emails || "E-Mails werden ausgewertet";
  if (value === "summarizing_message") return strings.cockpit_report_progress_chats || "Chat-Nachrichten werden ausgewertet";
  if (value === "summarizing_attachment") return strings.cockpit_report_progress_documents || "Anhänge und Dokumente werden ausgewertet";
  if (value === "summarizing_todo") return strings.cockpit_report_progress_tasks || "Aufgaben werden ausgewertet";
  if (value.startsWith("summarizing")) return strings.cockpit_report_progress_summarizing || "Inhalte werden ausgewertet";
  if (value === "reading_stored_text") return strings.cockpit_report_progress_reading || "Bereits ausgewertete Inhalte werden übernommen";
  if (value === "collecting") return strings.cockpit_report_progress_collecting || "E-Mails, Chats und Aufgaben deiner Projekte werden gesammelt";
  if (value === "priorities") return strings.cockpit_report_progress_priorities || "Prioritäten, Fristen und Entscheidungen werden zusammengestellt";
  return strings.cockpit_report_progress_queued || "Der Bericht wird vorbereitet";
}

function ReportProgress({ strings, progress }) {
  const phase = progress && progress.phase;
  const itemCount = progress ? Number(progress.itemCount) || 0 : 0;
  const value = progress && progress.value != null ? progress.value : phaseProgress(phase, itemCount);
  const label = phaseLabel(phase, strings);
  return (
    <div className="cockpit-report-progress">
      <div className="cockpit-report-progress-title">
        <MaterialIcon icon="auto_awesome" size={18} color="currentColor" weight={400} />
        <span>{strings.cockpit_report_creating_title || "Dein Tagesbericht entsteht"}</span>
      </div>
      <div className="cockpit-report-progress-phase">{label}</div>
      <div
        className="cockpit-report-progress-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
        aria-label={label}
      >
        <div className="cockpit-report-progress-fill" style={{ "--cockpit-report-progress": `${value}%` }} />
      </div>
      <div className="cockpit-report-progress-count">
        {progress && progress.live && itemCount > 0
          ? `${fill(
            itemCount === 1
              ? strings.cockpit_report_progress_count_one || "1 Inhalt ausgewertet"
              : strings.cockpit_report_progress_count_many || "{{count}} Inhalte ausgewertet",
            { count: itemCount }
          )} · `
          : ""}
        {strings.cockpit_report_progress_hint || "Das dauert meist ein bis zwei Minuten – du kannst das Cockpit währenddessen weiter nutzen."}
      </div>
    </div>
  );
}

/**
 * The report's own outline while it is still being fetched: a summary, the
 * figures and a few priority rows. Holding that shape means the card does not
 * jump when the real text lands on top of it.
 */
function ReportSkeleton() {
  return (
    <div className="cockpit-report-skeleton" aria-hidden="true">
      <Skeleton height={16} width="38%" />
      <Skeleton height={12} width="95%" />
      <Skeleton height={12} width="90%" />
      <Skeleton height={12} width="62%" />
      <div className="cockpit-report-skeleton-figures">
        <Skeleton height={64} />
        <Skeleton height={64} />
      </div>
      <Skeleton height={14} width="30%" />
      <Skeleton height={62} />
      <Skeleton height={62} />
      <Skeleton height={62} />
    </div>
  );
}

function extractSingleMessage(data) {
  if (!data || typeof data !== "object") return null;
  if (data.message && typeof data.message === "object") return data.message;
  if (data.data && typeof data.data === "object" && (data.data.id != null || data.data.subject !== undefined)) return data.data;
  if (data.id != null || data.subject !== undefined) return data;
  return null;
}

export default function CockpitDailyReportCard({
  strings,
  language,
  reports,
  latest,
  email,
  hasMailbox,
  loading,
  onMarkRead,
  onDelete,
  onConnectMailbox,
  rooms,
  reportRooms,
  notedPriorities,
  dismissedPriorities,
  onPriorityDismissed,
  bookmarks,
  onTodoCreated,
  onTaskChanged,
  embedded = false,
  roomFilter = null,
  onRoomFilterChange,
  onRefreshReport,
  refreshingReport = false,
  roomSelected = false,
  creatingReport = false,
  reportProgress = null,
  onCreateReport,
  tasksTarget = null,
  tasksActionTarget = null,
  onGroupCounts = null,
  taskGroups = null,
  taskDay = null,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const openedLatest = useRef(false);
  const [showPrevious, setShowPrevious] = useState(false);
  // Full messages (with body) loaded on expand, keyed by id.
  const [bodies, setBodies] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const items = useMemo(() => {
    const list = Array.isArray(reports) ? reports.filter(reportId) : [];
    const byId = new Map();
    const keepRicher = (row) => {
      if (!row || !reportId(row)) return;
      const key = String(reportId(row));
      const prev = byId.get(key);
      if (!prev) {
        byId.set(key, row);
        return;
      }
      const prevHtml = reportHtml(prev);
      const nextHtml = reportHtml(row);
      const richer = nextHtml.length >= prevHtml.length ? row : prev;
      const other = richer === row ? prev : row;
      byId.set(key, {
        ...other,
        ...richer,
        bodyHtml: richer.bodyHtml || other.bodyHtml || "",
        bodyText: richer.bodyText || other.bodyText || "",
        body: richer.body || other.body || "",
        snippet: richer.snippet || other.snippet || "",
        roomName: richer.roomName || other.roomName || "",
      });
    };
    list.forEach(keepRicher);
    keepRicher(latest);
    return [...byId.values()].sort(
      (a, b) => new Date(b.receivedAt || b.date || 0) - new Date(a.receivedAt || a.date || 0)
    );
  }, [reports, latest]);

  const current = items[0] || null;
  const previous = items.slice(1);

  const hour = email?.dailyBriefingHour != null ? email.dailyBriefingHour : 18;
  const hint = fill(strings.cockpit_report_time_hint || "Der Tagesbericht wird täglich um {{hour}}:00 Uhr erstellt.", {
    hour: String(hour).padStart(2, "0"),
  });

  const loadBody = useCallback(
    async (report) => {
      const id = reportId(report);
      const cached = bodies[String(id)];
      if (!id || isFullReport(report) || isFullReport(cached)) return;
      setLoadingId(id);
      try {
        const params = {};
        const accountId = report.emailAccountId || report.emailAccount;
        if (accountId) params.accountId = accountId;
        const res = await callAPI(`/email/messages/${encodeURIComponent(String(id))}`, params, "GET");
        const full = extractSingleMessage(res?.data) || extractSingleMessage(res);
        if (full && reportHtml(full).trim()) setBodies((prev) => ({ ...prev, [String(id)]: full }));
      } catch (_) {
        /* the row falls back to the text preview */
      } finally {
        setLoadingId((cur) => (cur === id ? null : cur));
      }
    },
    [bodies]
  );

  // The newest report is first. Open it once the list arrives so the cockpit
  // shows that document immediately.
  useEffect(() => {
    if (openedLatest.current || !items.length) return undefined;
    const first = items[0];
    const id = reportId(first);
    if (!id) return undefined;
    openedLatest.current = true;
    setExpandedId(id);
    if (!isRead(first) && onMarkRead) onMarkRead(first);
    loadBody(first);
    return undefined;
  }, [items, loadBody, onMarkRead]);

  const toggle = (report) => {
    const id = reportId(report);
    if (!id) return;
    if (String(expandedId) === String(id)) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!isRead(report) && onMarkRead) onMarkRead(report);
    loadBody(report);
  };

  /* The report folds the bookmarked priorities into its own list. Until its
     body is there the list still has to be shown, so it gets its own heading
     (the "Heute zu erledigen" card brings one of its own). */
  // A later day of the cockpit's day strip (`taskDay`): only what falls on it.
  const shownBookmarks = taskGroups && bookmarks
    ? bookmarks.filter((item) => taskGroups.includes(cockpitGroupOf(item && item.bookmarkCategory)))
      .filter((item) => !taskDay || cockpitTaskDay(item) === taskDay)
    : bookmarks;
  const bookmarkList =
    shownBookmarks && shownBookmarks.length ? (
      <section className="cockpit-briefing-section" id={BOOKMARKED_PRIORITIES_ID}>
        {tasksTarget ? null : <h4>{strings.cockpit_bookmarked_priorities || "Vorgemerkte Aufgaben"}</h4>}
        <CockpitBookmarkedPriorities strings={strings} rooms={rooms} items={shownBookmarks} onChanged={onTaskChanged} />
      </section>
    ) : null;
  // With a "Meine Aufgaben" card beside the report the list lives there.
  const bookmarkSection = bookmarkList && tasksTarget ? createPortal(bookmarkList, tasksTarget) : bookmarkList;

  const renderDocument = (report) => {
    const id = reportId(report);
    const cached = bodies[String(id)];
    const overviewCopy =
      latest && reportId(latest) && String(reportId(latest)) === String(id) && isFullReport(latest) ? latest : null;
    const full = isFullReport(cached) ? cached : isFullReport(report) ? report : overviewCopy;
    const asOf = formatAsOf(report.receivedAt || report.date, language);
    return (
      <div className="cockpit-report-document cockpit-report-document--lead">
        {full ? (
          /* Keyed on the report so switching projects replays the reveal. */
          <div className="cockpit-report-reveal" key={String(id)}>
            <CockpitBriefingText
              email={full}
              rooms={rooms}
              strings={strings}
              noted={notedPriorities}
              dismissed={dismissedPriorities}
              onDismiss={onPriorityDismissed}
              bookmarks={bookmarks}
              onCreated={onTodoCreated}
              onTaskChanged={onTaskChanged}
              toolbar={roomToolbar}
              // As a project's Übersicht: "Heute wichtig", the Top 5 as cards in a row.
              prioritiesOnly
              priorityCards
              // One project's report: every row is that project's, the picker names it.
              hideProject={roomSelected}
              fazitTitle={strings.room_overview_today_title || "Heute wichtig"}
              fazitMeta={asOf ? fill(strings.room_overview_as_of || "Stand: {{time}}", { time: asOf }) : null}
              tasksTarget={tasksTarget}
              tasksActionTarget={tasksActionTarget}
              onGroupCounts={onGroupCounts}
              taskGroups={taskGroups}
              taskDay={taskDay}
            />
          </div>
        ) : String(loadingId) === String(id) ? (
          <>
          {roomToolbar}
          <ReportSkeleton />
          {bookmarkSection}
          </>
        ) : (
          <>
          {roomToolbar}
          <div className="cockpit-report-loading">
            {report.snippet || stripHtml(report.bodyPreview, 600)}
          </div>
          {bookmarkSection}
          </>
        )}
      </div>
    );
  };

  const removeReport = async (report) => {
    const id = reportId(report);
    if (!id || !onDelete || String(deletingId) === String(id)) return;
    setDeletingId(id);
    try {
      const removed = await onDelete(report);
      if (!removed) return;
      setExpandedId((cur) => (String(cur) === String(id) ? null : cur));
      setBodies((prev) => {
        if (!(String(id) in prev)) return prev;
        const next = { ...prev };
        delete next[String(id)];
        return next;
      });
    } finally {
      setDeletingId((cur) => (String(cur) === String(id) ? null : cur));
    }
  };

  const deleteAction = (report) => {
    if (!onDelete) return null;
    const id = reportId(report);
    const busy = String(deletingId) === String(id);
    const label = strings.cockpit_report_delete || "Tagesbericht löschen";
    return (
      <button
        type="button"
        className={`cockpit-report-delete kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--danger${busy ? " cockpit-icon-btn--busy" : ""}`}
        disabled={busy}
        title={label}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          removeReport(report);
        }}
      >
        <MaterialIcon icon="delete" size={18} color="currentColor" />
      </button>
    );
  };

  const renderPrevious = (report) => {
    const id = reportId(report);
    const expanded = String(expandedId) === String(id);
    const cached = bodies[String(id)];
    const full = isFullReport(cached) ? cached : isFullReport(report) ? report : null;
    const unread = !isRead(report);
    return (
      <li key={id} className={`cockpit-report-item${expanded ? " cockpit-report-item--expanded" : ""}`}>
        <div
          className="cockpit-report-row"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => toggle(report)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggle(report);
            }
          }}
        >
          <span className={`cockpit-report-dot${unread ? " cockpit-report-dot--unread" : ""}`} />
          <div className="cockpit-report-body">
            <div className="cockpit-report-line">
              <span className="cockpit-report-subject">{report.subject || strings.email_tag_daily_briefing || "Tagesbericht"}</span>
              {deleteAction(report)}
              <span className="cockpit-report-time">{formatRelativeDay(report.receivedAt || report.date, strings, language)}</span>
            </div>
            <div className="cockpit-report-meta">
              <Chip tone="slate">{roomLabel(report, strings)}</Chip>
              {unread ? <Chip tone="accent">{strings.cockpit_report_unread || "Neu"}</Chip> : null}
            </div>
            {!expanded ? (
              <div className="cockpit-report-preview">
                {report.snippet || stripHtml(report.bodyHtml || report.bodyText || report.bodyPreview, 220)}
              </div>
            ) : null}
          </div>
          <MaterialIcon icon={expanded ? "expand_less" : "expand_more"} size={20} color="#7c8493" />
        </div>
        {expanded ? (
          <div className="cockpit-report-document">
            {full ? (
              <div className="cockpit-report-reveal">
                <CockpitBriefingText email={full} rooms={rooms} strings={strings} summaryOnly />
              </div>
            ) : String(loadingId) === String(id) ? (
              <ReportSkeleton />
            ) : (
              <div className="cockpit-report-loading">
                {report.snippet || stripHtml(report.bodyPreview, 600)}
              </div>
            )}
          </div>
        ) : null}
      </li>
    );
  };

  const reportProjects = reportRooms || [];
  const clearProjectLabel = strings.cockpit_report_clear_project || "Alle Projekte anzeigen";
  // A project without a report creates it from the empty state; the refresh
  // button only re-creates one that is there.
  const showRefresh = !!onRefreshReport && !(roomSelected && !current);
  const roomToolbar = (reportProjects.length && onRoomFilterChange) || showRefresh ? (
        <div className={`cockpit-report-create${roomSelected ? " cockpit-report-create--project" : ""}`}>
          {reportProjects.length && onRoomFilterChange ? (
            <div className={`cockpit-field cockpit-report-project${roomSelected ? " is-active" : ""}`}>
              <label className="cockpit-sr" htmlFor="cockpit-report-project">
                {strings.cockpit_filter_project || "Projekt"}
              </label>
              {roomSelected ? (
                <MaterialIcon icon="folder_open" size={18} color="currentColor" weight={400} className="cockpit-report-project-icon" />
              ) : null}
              <select
                id="cockpit-report-project"
                className={`cockpit-select${roomSelected ? " cockpit-select--active" : ""}`}
                value={roomFilter || ""}
                disabled={creatingReport}
                // TEMP debug for the project picker snapping back in Firefox.
                ref={(node) => console.log("[cockpit-debug] select", node ? "attached" : "detached")}
                onFocus={() => console.log("[cockpit-debug] select focus")}
                onBlur={() => console.log("[cockpit-debug] select blur")}
                onInput={(e) => console.log("[cockpit-debug] select input", e.target.value)}
                onChange={(e) => {
                  console.log("[cockpit-debug] select change", e.target.value);
                  onRoomFilterChange(e.target.value || null);
                }}
              >
                <option value="">{strings.cockpit_filter_all_projects || "Alle Projekte"}</option>
                {reportProjects.map((r) => (
                  <option key={r.id} value={String(r.id)}>
                    {r.name}
                  </option>
                ))}
              </select>
              {roomSelected ? (
                <button
                  type="button"
                  className="cockpit-report-project-clear"
                  onClick={() => onRoomFilterChange(null)}
                  disabled={creatingReport}
                  title={clearProjectLabel}
                  aria-label={clearProjectLabel}
                >
                  <MaterialIcon icon="close" size={16} color="currentColor" weight={500} />
                </button>
              ) : null}
            </div>
          ) : null}
          {showRefresh ? (
            <button
              type="button"
              className={`kj-btn kj-btn--secondary kj-btn--icon kj-btn--lg${refreshingReport ? " cockpit-icon-btn--busy" : ""}`}
              onClick={onRefreshReport}
              disabled={refreshingReport || creatingReport}
              title={strings.cockpit_report_recreate || "Tagesbericht neu erstellen"}
              aria-label={strings.cockpit_report_recreate || "Tagesbericht neu erstellen"}
            >
              <MaterialIcon icon="refresh" size={20} color="currentColor" weight={400} />
            </button>
          ) : null}
        </div>
  ) : null;

  const body = (
    <>
      {current ? null : roomToolbar}
      {creatingReport ? (
        <ReportProgress strings={strings} progress={reportProgress} />
      ) : null}
      {current ? (
        <>
          {renderDocument(current)}
          {previous.length ? (
            <div className="cockpit-report-footer">
              <button type="button" className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--sm" onClick={() => setShowPrevious((v) => !v)}>
                <span>
                  {showPrevious
                    ? strings.cockpit_report_hide_previous || "Vorherige Tagesberichte ausblenden"
                    : strings.cockpit_report_show_previous || "Vorherige Tagesberichte anzeigen"}
                </span>
                <MaterialIcon icon={showPrevious ? "expand_less" : "expand_more"} size={14} color="currentColor" />
              </button>
            </div>
          ) : null}
          {showPrevious && previous.length ? (
            <ul className="cockpit-report-list">{previous.map(renderPrevious)}</ul>
          ) : null}
        </>
      ) : creatingReport ? null : loading ? (
        <ReportSkeleton />
      ) : (
        <>
        {bookmarkSection}
        <EmptyState
          icon="summarize"
          title={
            roomSelected
              ? strings.cockpit_report_room_empty || "Noch kein Tagesbericht für dieses Projekt"
              : strings.cockpit_report_empty || "Noch kein Tagesbericht"
          }
          hint={
            roomSelected
              ? strings.cockpit_report_room_empty_hint || "Erstelle einen Tagesbericht nur für dieses Projekt."
              : hasMailbox
                ? hint
                : strings.cockpit_report_needs_mailbox || "Verbinde ein E-Mail-Konto, damit Kjuup täglich einen Tagesbericht mit automatischen Aufgaben erstellt."
          }
          action={
            roomSelected && onCreateReport ? (
              <SecondaryButton icon="summarize" onClick={onCreateReport} disabled={creatingReport}>
                {strings.cockpit_report_create || "Tagesbericht erstellen"}
              </SecondaryButton>
            ) : !roomSelected && !hasMailbox && onConnectMailbox ? (
              <SecondaryButton icon="mail" onClick={onConnectMailbox}>
                {strings.cockpit_email_connect || "E-Mail verbinden"}
              </SecondaryButton>
            ) : null
          }
        />
        </>
      )}
    </>
  );

  if (embedded) {
    return <div className={`cockpit-report cockpit-report--embedded${expandedId ? " cockpit-report--expanded" : ""}`}>{body}</div>;
  }
  return <Card className={`cockpit-report${expandedId ? " cockpit-report--expanded" : ""}`}>{body}</Card>;
}
